import * as THREE from 'three';
import fieldJson from '../../../scenes/field.json';
import { deriveFieldObjectLayouts } from '../../scene/fieldLayout';
import {
  parseFieldScene,
  type FieldSceneDefinition,
  type SceneMaterialDefinition,
} from '../../scene/fieldSchema';
import { applyRenderQuality, type RenderQuality } from '../RenderQuality';

export interface SceneBundle {
  scene: THREE.Scene;
  quality: RenderQuality;
  shadowMapSize: number;
  /** Passed to the renderer constructor by the app shell. */
  antialias: boolean;
  fogEnabled: boolean;
  readonly fogTemplate: THREE.Fog;
  dispose(): void;
}

const standardMaterial = (definition: SceneMaterialDefinition): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({
    color: new THREE.Color(definition.color),
    roughness: definition.roughness,
    metalness: definition.metalness,
  });

const addGround = (scene: THREE.Scene, field: FieldSceneDefinition): void => {
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(field.ground.size[0], field.ground.size[1]),
    standardMaterial(field.ground.material),
  );
  ground.name = 'ground';
  ground.position.z = field.ground.z;
  ground.receiveShadow = true;
  scene.add(ground);
};

const addProceduralObjects = (scene: THREE.Scene, field: FieldSceneDefinition): void => {
  for (const layout of deriveFieldObjectLayouts(field)) {
    const group = new THREE.Group();
    group.name = layout.name;
    group.position.set(...layout.position);
    group.rotation.z = layout.yaw;
    const material = standardMaterial(layout.material);

    for (const box of layout.boxes) {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(...box.dimensions), material);
      mesh.name = `${layout.name}:${box.name}`;
      mesh.position.set(...box.center);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      group.add(mesh);
    }
    scene.add(group);
  }
};

const addLighting = (scene: THREE.Scene, field: FieldSceneDefinition): void => {
  const ambient = new THREE.AmbientLight(
    new THREE.Color(field.lighting.ambientColor),
    field.lighting.ambientIntensity,
  );
  ambient.name = 'ambient';
  scene.add(ambient);

  const direction = new THREE.Vector3(...field.lighting.sunDirection).normalize();
  const sun = new THREE.DirectionalLight(
    new THREE.Color(field.lighting.sunColor),
    field.lighting.sunIntensity,
  );
  sun.name = 'sun';
  sun.position.copy(direction).multiplyScalar(-80);
  sun.castShadow = true;
  const shadowExtent = Math.max(field.ground.size[0], field.ground.size[1]) / 2;
  sun.shadow.camera.left = -shadowExtent;
  sun.shadow.camera.right = shadowExtent;
  sun.shadow.camera.top = shadowExtent;
  sun.shadow.camera.bottom = -shadowExtent;
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 250;
  sun.shadow.bias = -0.0004;
  scene.add(sun);

  sun.target.name = 'sun-target';
  sun.target.position.set(0, 0, field.ground.z);
  scene.add(sun.target);
};

const disposeScene = (scene: THREE.Scene): void => {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  scene.traverse((object) => {
    if (object instanceof THREE.Mesh) {
      geometries.add(object.geometry);
      const meshMaterials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of meshMaterials) {
        materials.add(material);
        for (const value of Object.values(material)) {
          if (value instanceof THREE.Texture) textures.add(value);
        }
      }
    }
    if (object instanceof THREE.DirectionalLight) object.shadow.map?.dispose();
  });
  for (const geometry of geometries) geometry.dispose();
  for (const texture of textures) texture.dispose();
  for (const material of materials) material.dispose();
  scene.clear();
};

export const buildSceneFromDefinition = (
  field: FieldSceneDefinition,
  quality: RenderQuality = 'med',
): SceneBundle => {
  const scene = new THREE.Scene();
  scene.name = field.id;
  scene.up.set(0, 0, 1);

  // A local, procedural solid sky uses both configured horizon and zenith
  // colours; it requires no HDRI, image request, canvas, or WebGL context.
  scene.background = new THREE.Color(field.skybox.bottomColor).lerp(
    new THREE.Color(field.skybox.topColor),
    0.65,
  );
  scene.userData.skybox = { ...field.skybox };

  addGround(scene, field);
  addProceduralObjects(scene, field);
  addLighting(scene, field);

  const fogTemplate = new THREE.Fog(field.fog.color, field.fog.near, field.fog.far);
  const bundle: SceneBundle = {
    scene,
    quality,
    shadowMapSize: 0,
    antialias: false,
    fogEnabled: false,
    fogTemplate,
    dispose: () => disposeScene(scene),
  };
  applyRenderQuality(bundle, quality);
  return bundle;
};

export const buildScene = (sceneId: string, quality: RenderQuality = 'med'): SceneBundle => {
  if (sceneId !== 'field') throw new Error(`Unknown scene: ${sceneId}`);
  return buildSceneFromDefinition(parseFieldScene(fieldJson), quality);
};
